import { useEffect, useState } from 'react'
import { useT } from '@/i18n/I18nProvider'
import type { TranslationKey } from '@/i18n/dictionary'
import { KaproMark } from './Logo'
import { cn } from '@/lib/utils'

/**
 * The loading screen shown while the app gets itself ready.
 *
 * The progress bar tracks **real milestones**, not a timer. A bar that fills on
 * a `setInterval` is a lie: it reaches 100% and then the screen sits there, or
 * it snaps to the end having shown nothing useful. Each step here is an event
 * that actually happened, so the bar stalling somewhere is genuine information
 * about what is slow — which on plant wifi is worth knowing.
 */
const STEPS = [
  { key: 'boot', labelKey: 'boot.starting', weight: 20 },
  { key: 'fonts', labelKey: 'boot.loadingLayout', weight: 20 },
  { key: 'assets', labelKey: 'boot.loadingAssets', weight: 25 },
  { key: 'session', labelKey: 'boot.checkingSession', weight: 35 },
] as const satisfies readonly { key: string; labelKey: TranslationKey; weight: number }[]

type StepKey = (typeof STEPS)[number]['key']

/**
 * Watches the browser's own readiness signals.
 *
 * `sessionReady` is passed in rather than watched, because only the auth
 * context knows when the session check has settled.
 */
function useLoadProgress(sessionReady: boolean) {
  const [done, setDone] = useState<Set<StepKey>>(new Set())

  const mark = (key: StepKey) =>
    setDone((current) => (current.has(key) ? current : new Set(current).add(key)))

  useEffect(() => {
    // The React tree is rendering, so the bundle parsed and executed.
    mark('boot')

    /*
     * Fonts, because this is the step that actually causes the visible flash.
     * Painting the app before the webfont resolves means every label reflows a
     * moment later. `document.fonts` is missing in a few older webviews, so an
     * absent API counts as done rather than blocking forever.
     */
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => mark('fonts'))
    } else {
      mark('fonts')
    }

    // Stylesheets, images and any chunk requested during the initial render.
    if (document.readyState === 'complete') {
      mark('assets')
    } else {
      const onLoad = () => mark('assets')
      window.addEventListener('load', onLoad, { once: true })

      return () => window.removeEventListener('load', onLoad)
    }
  }, [])

  useEffect(() => {
    if (sessionReady) mark('session')
  }, [sessionReady])

  const progress = STEPS.reduce((sum, step) => (done.has(step.key) ? sum + step.weight : sum), 0)
  const current = STEPS.find((step) => !done.has(step.key))

  return { progress, labelKey: current?.labelKey ?? ('boot.ready' as const), finished: progress >= 100 }
}

export function SplashScreen({ sessionReady }: { sessionReady: boolean }) {
  const { progress, labelKey, finished } = useLoadProgress(sessionReady)
  const t = useT()
  const label = t(labelKey)

  /*
   * Kept on screen for a beat after finishing, then faded out.
   *
   * Without the delay a fast load produces a flash of splash — worse than no
   * splash at all. The element stays mounted through the fade so the bar can
   * be seen reaching the end rather than vanishing at 80%.
   */
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (!finished) return

    const timer = setTimeout(() => setHidden(true), 400)

    return () => clearTimeout(timer)
  }, [finished])

  if (hidden) return null

  return (
    <div
      className={cn(
        'bg-background fixed inset-0 z-100 flex flex-col items-center justify-center gap-8',
        'transition-opacity duration-300',
        finished ? 'pointer-events-none opacity-0' : 'opacity-100',
      )}
      // A live region rather than a bare spinner: someone using a screen reader
      // gets told what is happening and when it is done.
      role="status"
      aria-live="polite"
      aria-label={t('boot.aria', { step: label })}
    >
      <style>{`
        @keyframes kapro-spin-3d {
          from { transform: rotateY(0deg); }
          to   { transform: rotateY(360deg); }
        }
        /* Respects the OS setting. A rotating logo is exactly the kind of
           motion that triggers nausea for some people. */
        @media (prefers-reduced-motion: reduce) {
          .kapro-spinner { animation: none !important; }
        }
      `}</style>

      {/* perspective on the parent is what makes the rotation read as depth
          rather than as a horizontal squash. */}
      <div style={{ perspective: '600px' }}>
        <div
          className="kapro-spinner relative size-24"
          style={{
            transformStyle: 'preserve-3d',
            animation: 'kapro-spin-3d 2.4s linear infinite',
          }}
        >
          {/*
            Two faces, not one.
            A single plane disappears completely every half turn — it has no
            thickness — which reads as a flicker rather than a spin. The second
            copy is pre-rotated so that as the front turns away, the back turns
            into view.
          */}
          <KaproMark
            className="absolute inset-0 size-24 drop-shadow-lg"
            style={{ backfaceVisibility: 'hidden' }}
          />
          <KaproMark
            className="absolute inset-0 size-24 drop-shadow-lg"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          />
        </div>
      </div>

      <div className="w-56 max-w-[70vw]">
        <div className="bg-muted h-1.5 overflow-hidden rounded-full">
          <div
            className="bg-brand-600 h-full rounded-full transition-[width] duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <p className="muted mt-3 text-center text-xs tabular-nums">
          {label}
          {!finished && ` — ${progress}%`}
        </p>
      </div>
    </div>
  )
}
